import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import './ContractAwardNotices.css';
import { fetchAwardNoticeList } from '../../services/awardNoticeApi';
import AuctionList from '../../components/AuctionList/AuctionList';
import logo from '../../assets/icon.png';
import Breadcrumbs from '../../components/Breadcrumbs';
import ListLoadingHolder from '../../components/LoadingHolders/ListLoadingHolder';

const ContractAwardNotices = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL parameters
  const getInitialFilters = () => ({
    auctionId: searchParams.get('auctionId') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    pageIndex: parseInt(searchParams.get('pageIndex') || '0'),
    pageSize: parseInt(searchParams.get('pageSize') || '10'),
  });

  const [filters, setFilters] = useState(getInitialFilters());
  const [errors, setErrors] = useState([]);
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Update URL when filters change
  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        value !== 0
      ) {
        // Don't include pageIndex: 0 or pageSize: 10 (defaults)
        if (
          (key === 'pageIndex' && value === 0) ||
          (key === 'pageSize' && value === 10)
        ) {
          return;
        }
        params[key] = value.toString();
      }
    });
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const validateValues = useCallback(() => {
    const validationErrors = [];

    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      if (startDate > endDate) {
        validationErrors.push(
          'Data de început nu poate fi mai mare decât data de sfârşit.',
        );
      }
    }

    setErrors(validationErrors);
    return validationErrors.length === 0;
  }, [filters]);

  // Auto-fetch when filters change
  useEffect(() => {
    let cancelled = false;
    if (!validateValues()) return;
    setLoading(true);
    fetchAwardNoticeList(filters)
      .then((data) => {
        if (cancelled) return;
        setResults(data.results || []);
        setTotalResults(data.totalResults || 0);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Error fetching search results:', error);
        setErrors(['Eroare la obținerea datelor. Vă rugăm încercați din nou.']);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Manual handler for buttons — updates filters, which triggers the effect above.
  const handleFilter = useCallback(() => {
    // no-op: filter changes drive fetches via the effect above
  }, []);

  useEffect(() => {
    if (window.prerenderReady !== undefined) {
      window.prerenderReady = true;
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
      pageIndex: 0,
    }));
  };

  const handleReset = () => {
    setFilters({
      auctionId: '',
      startDate: '',
      endDate: '',
      pageIndex: 0,
      pageSize: 10,
    });
    setErrors([]);
    setResults([]);
    setTotalResults(0);
    handleFilter();
  };

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { label: 'Anunturi de Atribuire' },
  ];

  const pageTitle = 'Anunturi de Atribuire - Licitatii-Constructii';
  const pageDescription =
    'Descoperă cele mai recente anunturi de atribuire pentru proiecte.';
  const pageUrl = `${import.meta.env.VITE_DOMAIN}/anunturi-atribuire`;

  const canonicalUrl = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'pageIndex') return;
      if (key === 'pageSize' && value === 10) return;

      if (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        value !== 0
      ) {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    return `${window.location.origin}/anunturi-atribuire${queryString ? '?' + queryString : ''}`;
  }, [filters]);

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageUrl={pageUrl}
        canonicalUrl={canonicalUrl}
        logoUrl={logo}
      />
      <section className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="container max-w-5xl mx-auto">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-700 mb-4 text-center">
            Anunturi de Atribuire - Contracte Adjudecate
          </h1>
          <p className="text-lg text-center text-gray-600 mb-8">
            Cauta și descoperă anunturi de atribuire pentru proiecte publice și
            private.
          </p>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border-t-8 border-indigo-500 mb-10 animate-fade-in">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 text-white px-4 py-3 rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-green-600 transition-all flex justify-between items-center mb-4"
            >
              {isFilterOpen ? 'Ascunde filtrele ▲' : 'Afișează filtrele ▼'}
            </button>
            {isFilterOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    ID Anunț (CAN)
                  </label>
                  <input
                    type="text"
                    name="auctionId"
                    value={filters.auctionId}
                    onChange={handleInputChange}
                    placeholder="ex: CAN1161889"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Data de Început
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Data de Sfârşit
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
            )}
          </div>

          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-fade-in">
            <div className="flex items-center justify-end space-x-4 border-b pb-5 mb-8">
              <button
                onClick={() => handleFilter()}
                className="bg-gradient-to-r from-indigo-500 to-green-500 text-white px-6 py-2 rounded-xl font-bold hover:from-indigo-600 hover:to-green-600 transition-all"
              >
                Filtrează
              </button>
              <button
                onClick={handleReset}
                className="bg-red-100 hover:bg-red-300 text-gray-700 px-6 py-2 rounded-xl font-bold hover:text-white duration-300"
              >
                Resetează
              </button>
            </div>

            <div className="mt-8">
              {loading ? (
                <ListLoadingHolder />
              ) : (
                <AuctionList
                  auctions={results}
                  totalResults={totalResults}
                  pageSize={filters.pageSize}
                  pageIndex={filters.pageIndex}
                  auctionType={'CANotices'}
                />
              )}
            </div>

            {/* PAGINATION */}
            {!loading && totalResults > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-2 mt-10">
                <button
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, pageIndex: 0 }))
                  }
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  Prima
                </button>
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex - 1,
                    }))
                  }
                  disabled={filters.pageIndex === 0}
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  &laquo;
                </button>
                {Array.from(
                  {
                    length: Math.min(
                      5,
                      Math.ceil(totalResults / filters.pageSize),
                    ),
                  },
                  (_, i) => {
                    let startPage = Math.max(
                      0,
                      Math.min(
                        filters.pageIndex - 2,
                        Math.ceil(totalResults / filters.pageSize) - 5,
                      ),
                    );
                    let page = startPage + i;
                    if (
                      page < 0 ||
                      page >= Math.ceil(totalResults / filters.pageSize)
                    )
                      return null;
                    return (
                      <button
                        key={page}
                        onClick={() =>
                          setFilters((prev) => ({
                            ...prev,
                            pageIndex: page,
                          }))
                        }
                        className={`px-3 py-2 rounded-lg font-bold transition ${
                          filters.pageIndex === page
                            ? 'bg-indigo-500 text-white'
                            : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        }`}
                      >
                        {page + 1}
                      </button>
                    );
                  },
                )}
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: prev.pageIndex + 1,
                    }))
                  }
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  &raquo;
                </button>
                <button
                  onClick={() =>
                    setFilters((prev) => ({
                      ...prev,
                      pageIndex: Math.ceil(totalResults / filters.pageSize) - 1,
                    }))
                  }
                  disabled={
                    filters.pageIndex >=
                    Math.ceil(totalResults / filters.pageSize) - 1
                  }
                  className="px-3 py-2 rounded-lg font-bold bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition disabled:opacity-50"
                >
                  Ultima
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ContractAwardNotices;
